// src/pages/PaymentMethodChoicePage.jsx
//
// Sprint 2c-2b-3: Payment method selection page for study pack purchases.
// Migrated verbatim from prototype/between-the-lines-v2.html lines 8135-8200.
//
// Architecture decision (Sprint 2c-2b-3 spec v1.1 Strategy A + C combined):
//   - Strategy A: pack identity flows via URL parameter (the router param hook).
//     The pack-id placeholder in the route signature lets the receiver
//     component resolve the pack object without any session-state field.
//   - Strategy C: defensive no-pack fallback UI renders when the URL param
//     is missing or resolves to a pack-id not in the available pack catalog.
//     Prevents crash and prevents invalid pending-purchases record creation.
//
// Migration adjustments per Sprint 2c-1 + 2c-2b-1 + 2c-2b-2 precedent:
//   - Prototype page-state setter prop dropped; routing now uses the
//     router navigate hook (two conversion sites: back-to-library and
//     PayMe/FPS continue).
//   - Prototype pack-id prop dropped; the URL parameter hook resolves
//     pack identity per Strategy A.
//   - The prototype's silent fallback to packs[0] when the requested
//     pack-id is missing is REPLACED by Strategy C explicit fallback UI
//     (semantic improvement: prevents silently rendering "Pay for [wrong
//     pack]").
//
// State-prop pattern (Sprint 2c-1 convention; Sprint 2c-2b-1 V1 amendment
// confirms direct ({ state }) prop, not the outlet context hook).
//
// Card payments return to the existing consent-gated Stripe checkout.
// PayMe/FPS keeps its manual confirmation flow. No financial writes here.

import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getIntervalSuffix, getIntervalDescription } from '../config/intervalOptions.js';

export const PaymentMethodChoicePage = ({ state }) => {
    const navigate = useNavigate();
    const { packId } = useParams();

    // Strategy C fallback: if URL param missing or pack-id not in state.packs,
    // render the no-pack-selected fallback UI.
    const pack = state.packs.find(p => p.id === packId);
    if (!pack) {
        return (
            <div className="page-fade-in min-h-[calc(100vh-4rem)] bg-burgundy-bg paper-texture">
                <div className="max-w-3xl mx-auto px-4 md:px-8 py-10 md:py-16 text-center">
                    <h1 className="font-display text-burgundy-text text-3xl mb-4">No pack selected</h1>
                    <p className="font-serif italic text-burgundy-muted mb-8">
                        Please choose a pack from your library to continue with payment.
                    </p>
                    <button onClick={() => navigate('/dashboard')}
                        className="px-5 py-3 bg-burgundy-accent text-burgundy-pageDark font-display text-sm hover:bg-burgundy-accentLight transition-colors btn-sweep font-semibold">
                        Browse Packs
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="page-fade-in min-h-[calc(100vh-4rem)] bg-burgundy-bg paper-texture">
            <div className="max-w-4xl mx-auto px-4 md:px-8 py-10 md:py-16">
                <button onClick={() => navigate('/dashboard')}
                    className="font-serif italic text-burgundy-muted text-sm hover:text-burgundy-accent transition-colors mb-6">
                    ← Back to Library
                </button>

                <div className="mb-8">
                    <div className="font-display text-burgundy-accent text-xs mb-2 font-semibold">Choose Payment Method</div>
                    <h1 className="font-display text-3xl md:text-5xl text-burgundy-text mb-2 leading-tight">{pack.label}</h1>
                    <p className="font-serif italic text-burgundy-muted text-lg">HK${pack.price}{getIntervalSuffix(pack.autoRenewInterval)} · {getIntervalDescription(pack.autoRenewInterval)}</p>
                </div>

                {state.cart && state.cart.length > 1 && (
                    <div className="border-2 border-amber-500/50 bg-amber-500/10 p-5 mb-6">
                        <div className="flex items-start gap-3">
                            <div className="font-display text-amber-300 text-xs shrink-0 pt-0.5 font-semibold">
                                ⚠ Important
                            </div>
                            <div className="font-serif text-amber-100 text-sm leading-relaxed space-y-2">
                                <p>
                                    <strong>PayMe/FPS covers ONE pack at a time.</strong> Your cart currently has {state.cart.length} packs.
                                </p>
                                <p>
                                    This payment will cover <strong className="text-amber-300">{pack.label} only</strong> (HK${pack.price}).
                                    The other {state.cart.length - 1} pack{state.cart.length - 1 === 1 ? '' : 's'} will <strong>remain in your cart</strong> for separate purchase.
                                </p>
                                <p className="font-serif italic text-amber-200/80 text-xs pt-1">
                                    For PayMe/FPS, repeat the process for each pack. To pay for your cart together, choose card checkout below.
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                <div className="grid md:grid-cols-2 gap-5">
                    {/* Return to the existing consent-gated checkout; no charge here. */}
                    <div className="border border-burgundy-accent/50 bg-burgundy-page p-6">
                        <div className="font-display text-burgundy-accent text-xs mb-3 font-semibold">Card · Stripe</div>
                        <div className="font-display text-burgundy-text text-xl mb-2">Secure card checkout</div>
                        <p className="font-serif italic text-burgundy-muted text-sm mb-6">Credit and debit cards. Apple Pay and Google Pay where available.</p>
                        <button onClick={() => {
                            const cart = state.cart || [];
                            if (!cart.some(item => item.id === pack.id)) state.setCart([...cart, pack]);
                            navigate('/checkout');
                        }} className="w-full py-2.5 bg-burgundy-accent text-burgundy-pageDark font-display text-xs font-semibold">
                            Continue to card checkout →
                        </button>
                        <p className="font-serif italic text-burgundy-muted text-xs mt-3 text-center">Automatic access after payment confirmation</p>
                    </div>

                    {/* PayMe / FPS — active */}
                    <button onClick={() => navigate('/pack-payment-payme/' + packId)}
                        className="text-left border border-burgundy-accent/50 bg-burgundy-page p-6 hover:bg-burgundy-pageDark transition-colors group">
                        <div className="font-display text-burgundy-accent text-xs mb-3 font-semibold">PayMe / FPS</div>
                        <div className="font-display text-burgundy-text text-xl mb-2">Manual confirmation</div>
                        <p className="font-serif italic text-burgundy-muted text-sm mb-6">PayMe · FPS · Bank transfer</p>
                        <div className="w-full py-2.5 bg-burgundy-accent text-burgundy-pageDark font-display text-[12px] text-center group-hover:bg-burgundy-accentLight transition-colors font-semibold text-xs">
                            Continue →
                        </div>
                        <p className="font-serif italic text-burgundy-muted text-xs mt-3 text-center">Manual confirmation · 8–12 hours</p>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PaymentMethodChoicePage;
